import React from 'react';
import { 
    Box, 
    Typography, 
    TextField, 
    Grid, 
    Paper,
    Button,
    InputAdornment,
    Tooltip,
    IconButton
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * Basic Information step for company onboarding
 */
export const BasicInfoStep = ({ 
    companyName,
    setCompanyName,
    website,
    setWebsite,
    phoneNumber,
    setPhoneNumber,
    logo,
    setLogo,
    domain,
    setDomain,
    errors
}) => {
    // Handle logo upload
    const handleLogoUpload = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            // Check if file is an image
            if (!file.type.startsWith('image/')) {
                alert('Please upload an image file');
                return;
            }
            // Check file size (e.g., max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                alert('File size should be less than 5MB');
                return;
            }
            setLogo(file);
            // Clear the input value to allow uploading the same file again if needed
            e.target.value = '';
        }
    };

    // Handle logo removal
    const handleLogoRemove = () => {
        setLogo(null);
    };

    return (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
                Company Information
            </Typography>
            
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Company Name"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        required
                        error={Boolean(errors?.companyName)}
                        helperText={errors?.companyName}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Phone Number"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        required
                        error={Boolean(errors?.phoneNumber)}
                        helperText={errors?.phoneNumber || "Official company phone number"}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Domain"
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        required
                        error={Boolean(errors?.domain)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title="This is your company's main domain. For example, if your emails in your company are johnSmith@company.com, your domain would be company.com">
                                        <IconButton edge="end">
                                            <HelpOutlineIcon />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Website"
                        type="url"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        error={Boolean(errors?.website)}
                        helperText={errors?.website}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            Company Logo <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        
                        <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="logo-upload"
                            type="file"
                            onChange={handleLogoUpload}
                            // Prevent multiple file selection
                            multiple={false}
                        />
                        
                        {!logo ? (
                            <label htmlFor="logo-upload">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    startIcon={<CloudUploadIcon />}
                                >
                                    Upload Logo
                                </Button>
                            </label>
                        ) : (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                                <img
                                    src={URL.createObjectURL(logo)}
                                    alt="Company Logo"
                                    style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'contain' }}
                                />
                                <Box sx={{ ml: 2 }}>
                                    <Typography variant="body2">
                                        {logo.name}
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        startIcon={<DeleteIcon />}
                                        onClick={handleLogoRemove}
                                        sx={{ mt: 1 }}
                                    >
                                        Remove
                                    </Button>
                                </Box>
                            </Box>
                        )}
                        
                        {errors?.logo && (
                            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                                {errors.logo}
                            </Typography>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Paper>
    );
}; 